import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, LogOut, Mail, Package, TrendingUp } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [earnings, setEarnings] = useState({ total_earnings: 0, total_orders: 0 });
  const [userLogs, setUserLogs] = useState([]);
  const [customerStats, setCustomerStats] = useState({ total_customers: 0, total_unique_logins: 0, new_customers_last_30_days: 0 });
  const [activeTab, setActiveTab] = useState('products');
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [brands, setBrands] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '' });

  useEffect(() => {
    fetchProducts();
    fetchEarnings();
    fetchUserLogs();
    fetchCustomerStats();
    fetchCategories();
    fetchBrands();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/admin/products`, {
        withCredentials: true
      });
      setProducts(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei prodotti');
    } finally {
      setLoading(false);
    }
  };

  const fetchEarnings = async () => {
    try {
      const response = await axios.get(`${API}/admin/earnings`, {
        withCredentials: true
      });
      setEarnings(response.data);
    } catch (error) {
      console.error('Errore nel caricamento incassi');
    }
  };

  const fetchUserLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/user-logs`, {
        withCredentials: true
      });
      setUserLogs(response.data);
    } catch (error) {
      console.error('Errore nel caricamento log accessi');
    }
  };

  const fetchCustomerStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/customer-stats`, {
        withCredentials: true
      });
      setCustomerStats(response.data);
    } catch (error) {
      console.error('Errore nel caricamento statistiche clienti');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo prodotto?')) return;

    try {
      await axios.delete(`${API}/admin/products/${productId}`, {
        withCredentials: true
      });
      toast.success('Prodotto eliminato');
      fetchProducts();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleSendDiscountEmail = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    try {
      const response = await axios.post(
        `${API}/admin/send-discount-email`,
        {
          product_ids: selectedProducts,
          subject: formData.get('subject'),
          message: formData.get('message')
        },
        { withCredentials: true }
      );

      if (response.data.status === 'sent') {
        toast.success(`Email inviate a ${response.data.recipients} clienti!`);
        setIsEmailDialogOpen(false);
        setSelectedProducts([]);
      } else if (response.data.status === 'skipped') {
        toast.warning('SMTP non configurato. Email non inviate.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio email');
    }
  };

  const discountedProducts = products.filter(p => p.discount_percentage > 0);

  // Filtra prodotti in base alla ricerca
  const filteredProducts = products.filter(product => {
    const query = searchQuery.toLowerCase();
    return (
      product.name.toLowerCase().includes(query) ||
      product.brand.toLowerCase().includes(query) ||
      product.category.toLowerCase().includes(query) ||
      product.description?.toLowerCase().includes(query)
    );
  });

  // ===== CATEGORIES CRUD FUNCTIONS =====
  
  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Errore nel caricamento delle categorie');
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await axios.get(`${API}/brands`);
      setBrands(response.data);
    } catch (error) {
      console.error('Error fetching brands:', error);
      toast.error('Errore nel caricamento dei marchi');
    }
  };

  const handleCreateCategory = async () => {
    try {
      await axios.post(`${API}/admin/categories`, categoryForm, {
        withCredentials: true
      });
      toast.success('Categoria creata con successo!');
      setIsCategoryDialogOpen(false);
      setCategoryForm({ name: '', description: '' });
      fetchCategories();
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error('Errore nella creazione della categoria');
    }
  };

  const handleUpdateCategory = async () => {
    try {
      await axios.put(`${API}/admin/categories/${editingCategory.category_id}`, categoryForm, {
        withCredentials: true
      });
      toast.success('Categoria aggiornata con successo!');
      setIsCategoryDialogOpen(false);
      setEditingCategory(null);
      setCategoryForm({ name: '', description: '' });
      fetchCategories();
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('Errore nell\'aggiornamento della categoria');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa categoria?')) return;
    
    try {
      await axios.delete(`${API}/admin/categories/${categoryId}`, {
        withCredentials: true
      });
      toast.success('Categoria eliminata con successo!');
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Errore nell\'eliminazione della categoria');
    }
  };

  const openCategoryDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({ name: category.name, description: category.description || '' });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: '', description: '' });
    }
    setIsCategoryDialogOpen(true);
  };



  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">

            <div className="flex items-center gap-4">
              <Package className="text-orange-600" size={32} />
              <div>
                <h1 className="text-2xl font-bold text-slate-800">Pannello Amministratore</h1>
                <p className="text-sm text-slate-600">Benvenuto, {user?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                onClick={() => navigate('/admin/categories')}
                className="bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
              >
                📁 Categorie
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/admin/brands')}
                className="bg-purple-50 border-purple-300 text-purple-700 hover:bg-purple-100"
              >
                🏢 Marchi
              </Button>
              <a href="/admin/controllo" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="bg-orange-50 border-orange-300 text-orange-700 hover:bg-orange-100">
                  📊 Pannello di Controllo
                </Button>
              </a>
              <a href="/" target="_blank" rel="noopener noreferrer">
                <Button variant="outline">Vai al Sito</Button>
              </a>
              <Button onClick={handleLogout} variant="outline" className="text-red-600 border-red-600 hover:bg-red-50">
                <LogOut size={18} className="mr-2" />
                Esci
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Prodotti Totali</p>
                  <p className="text-3xl font-bold text-slate-800">{products.length}</p>
                </div>
                <Package className="text-orange-600" size={40} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Prodotti in Sconto</p>
                  <p className="text-3xl font-bold text-slate-800">{discountedProducts.length}</p>
                </div>
                <TrendingUp className="text-green-600" size={40} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Incassi Totali</p>
                  <p className="text-3xl font-bold text-green-600">
                    €{earnings.total_earnings.toFixed(2)}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">{earnings.total_orders} ordini</p>
                </div>
                <TrendingUp className="text-green-600" size={40} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Customer Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-700 font-semibold">Clienti Totali</p>
                  <p className="text-4xl font-bold text-blue-600">{customerStats.total_customers}</p>
                  <p className="text-xs text-slate-600 mt-1">Registrati sulla piattaforma</p>
                </div>
                <div className="text-5xl">👥</div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-white border-2 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-700 font-semibold">Accessi Unici</p>
                  <p className="text-4xl font-bold text-green-600">{customerStats.total_unique_logins}</p>
                  <p className="text-xs text-slate-600 mt-1">Clienti che hanno fatto login</p>
                </div>
                <div className="text-5xl">🔐</div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-50 to-white border-2 border-orange-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-700 font-semibold">Nuovi (30gg)</p>
                  <p className="text-4xl font-bold text-orange-600">{customerStats.new_customers_last_30_days}</p>
                  <p className="text-xs text-slate-600 mt-1">Clienti registrati ultimo mese</p>
                </div>
                <div className="text-5xl">📈</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-4 mb-6">
          <ProductDialog
            product={null}
            categories={categories}
            brands={brands}
            onSuccess={fetchProducts}
            trigger={
              <Button className="bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600">
                <Plus size={18} className="mr-2" />
                Nuovo Prodotto
              </Button>
            }
          />
        </div>

        {/* Product Form / Email Dialog Section */}
        <div className="mb-8">
          {/* Email Dialog Trigger */}
          <Dialog open={isEmailDialogOpen} onOpenChange={setIsEmailDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50">
                <Mail size={18} className="mr-2" />
                Invia Email Sconti
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Invia Email Sconti ai Clienti</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSendDiscountEmail} className="space-y-4">
                <div>
                  <Label>Seleziona Prodotti in Sconto</Label>
                  <div className="mt-2 space-y-2 max-h-60 overflow-y-auto border rounded-lg p-3">
                    {discountedProducts.map(product => (
                      <label key={product.product_id} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedProducts.includes(product.product_id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedProducts([...selectedProducts, product.product_id]);
                            } else {
                              setSelectedProducts(selectedProducts.filter(id => id !== product.product_id));
                            }
                          }}
                          className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                        />
                        <span className="flex-1">{product.name} (-{product.discount_percentage}%)</span>
                        <span className="text-orange-600 font-semibold">€{product.price}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject">Oggetto Email</Label>
                  <Input
                    id="subject"
                    name="subject"
                    required
                    placeholder="Offerte Speciali Edil Service 2.0"
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="message">Messaggio Personalizzato</Label>
                  <Textarea
                    id="message"
                    name="message"
                    rows={4}
                    placeholder="Approfitta delle nostre offerte speciali!"
                    className="mt-2"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={selectedProducts.length === 0}
                >
                  Invia Email a Tutti i Clienti
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Products Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Gestione Prodotti</CardTitle>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Cerca prodotto, marca o categoria..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-80 pl-10"
                  />
                  <svg
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="8" cy="8" r="6" />
                    <path d="M15 15l-3-3" />
                  </svg>
                </div>
                {searchQuery && (
                  <span className="text-sm text-slate-600">
                    {filteredProducts.length} di {products.length} prodotti
                  </span>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Caricamento...</div>
            ) : products.length === 0 ? (
              <div className="text-center py-8 text-slate-600">
                Nessun prodotto. Usa "Popola Prodotti Esempio" per iniziare.
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-8 text-slate-600">
                Nessun prodotto trovato per "{searchQuery}"
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-3">Nome</th>
                      <th className="text-left p-3">Marca</th>
                      <th className="text-left p-3">Categoria</th>
                      <th className="text-right p-3">Prezzo</th>
                      <th className="text-right p-3">Sconto %</th>
                      <th className="text-right p-3">Disponibilità</th>
                      <th className="text-center p-3">Mostra Prezzo</th>
                      <th className="text-center p-3">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map(product => (
                      <tr key={product.product_id} className="border-t hover:bg-slate-50">
                        <td className="p-3 font-medium">{product.name}</td>
                        <td className="p-3 text-slate-600">{product.brand}</td>
                        <td className="p-3 text-slate-600">{product.category}</td>
                        <td className="p-3 text-right font-semibold">
                          {product.price ? `€${product.price.toFixed(2)}` : '-'}
                        </td>
                        <td className="p-3 text-right">
                          {product.discount_percentage > 0 && (
                            <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-sm font-semibold">
                              -{product.discount_percentage}%
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-right">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            product.availability_status === 'Disponibile in Magazzino' 
                              ? 'bg-green-100 text-green-700' 
                              : product.availability_status === 'Disponibile su Ordinazione'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {product.availability_status || 'Disponibile in Magazzino'}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          {product.show_price ? '✅' : '❌'}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center justify-center gap-2">
                            <ProductDialog
                              product={product}
                              categories={categories}
                              brands={brands}
                              onSuccess={fetchProducts}
                              trigger={
                                <Button size="sm" variant="outline">
                                  <Edit size={16} />
                                </Button>
                              }
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleDelete(product.product_id)}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* User Access Logs Section */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>🔐 Log Accessi Clienti e Amministratori</CardTitle>
            <p className="text-sm text-slate-600 mt-2">
              Traccia di tutti gli accessi sulla piattaforma (ultimi 100)
            </p>
          </CardHeader>
          <CardContent>
            {userLogs.length === 0 ? (
              <p className="text-slate-600 text-center py-8">Nessun log di accesso disponibile</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Email Utente</th>
                      <th className="text-left py-3 px-4">Data/Ora Accesso</th>
                      <th className="text-left py-3 px-4">Indirizzo IP</th>
                      <th className="text-left py-3 px-4">Browser/Dispositivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userLogs.map((log) => (
                      <tr key={log.log_id} className="border-b hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{log.user_email}</span>
                            {log.user_email.includes('edilvale') && (
                              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Admin</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {new Date(log.login_time).toLocaleString('it-IT', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td className="py-3 px-4 text-sm font-mono">{log.ip_address || 'N/A'}</td>
                        <td className="py-3 px-4 text-xs truncate max-w-xs text-slate-600">
                          {log.user_agent ? log.user_agent.substring(0, 60) + '...' : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

// Product Dialog Component
const ProductDialog = ({ product, categories, brands, onSuccess, trigger }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(
    product || {
      name: '',
      category: '',
      brand: '',
      description: '',
      price: '',
      discount_percentage: 0,
      availability_status: 'Disponibile in Magazzino',
      image_url: '',
      show_price: true
    }
  );

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      brand: '',
      description: '',
      price: '',
      discount_percentage: 0,
      availability_status: 'Disponibile in Magazzino',
      image_url: '',
      show_price: true
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const url = product
        ? `${API}/admin/products/${product.product_id}`
        : `${API}/admin/products`;
      
      const method = product ? 'put' : 'post';
      
      await axios[method](url, {
        ...formData,
        price: formData.price ? parseFloat(formData.price) : null,
        discount_percentage: parseInt(formData.discount_percentage) || 0
      }, { withCredentials: true });

      toast.success(product ? 'Prodotto aggiornato!' : 'Prodotto creato!');
      
      // Reset form solo se stiamo creando un nuovo prodotto (non in modifica)
      if (!product) {
        resetForm();
      }
      
      setOpen(false);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{product ? 'Modifica Prodotto' : 'Nuovo Prodotto'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Nome Prodotto *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label>Marca *</Label>
              <Select
                value={formData.brand}
                onValueChange={(value) => setFormData({...formData, brand: value})}
                required
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleziona marca" />
                </SelectTrigger>
                <SelectContent>
                  {brands.map(brand => (
                    <SelectItem key={brand.brand_id || brand.name} value={brand.name}>{brand.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Categoria *</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({...formData, category: value})}
                required
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleziona categoria" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.category_id || cat.name} value={cat.name}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Prezzo (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: e.target.value})}
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label>Descrizione</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={3}
              className="mt-1"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Sconto %</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={formData.discount_percentage}
                onChange={(e) => setFormData({...formData, discount_percentage: e.target.value})}
                className="mt-1"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch
                  checked={formData.show_price}
                  onCheckedChange={(checked) => setFormData({...formData, show_price: checked})}
                />
                <span className="text-sm">Mostra Prezzo</span>
              </label>
            </div>
          </div>

          <div>
            <Label>Stato Disponibilità *</Label>
            <div className="grid grid-cols-3 gap-3 mt-2">
              <Button
                type="button"
                variant={formData.availability_status === 'Disponibile su Ordinazione' ? 'default' : 'outline'}
                className={`${formData.availability_status === 'Disponibile su Ordinazione' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                onClick={() => setFormData({...formData, availability_status: 'Disponibile su Ordinazione'})}
              >
                Su Ordinazione
              </Button>
              <Button
                type="button"
                variant={formData.availability_status === 'Disponibile in Magazzino' ? 'default' : 'outline'}
                className={`${formData.availability_status === 'Disponibile in Magazzino' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                onClick={() => setFormData({...formData, availability_status: 'Disponibile in Magazzino'})}
              >
                In Magazzino
              </Button>
              <Button
                type="button"
                variant={formData.availability_status === 'Non disponibile' ? 'default' : 'outline'}
                className={`${formData.availability_status === 'Non disponibile' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                onClick={() => setFormData({...formData, availability_status: 'Non disponibile'})}
              >
                Non Disponibile
              </Button>
            </div>
          </div>

          <div>
            <Label>URL Immagine</Label>
            <Input
              type="url"
              value={formData.image_url}
              onChange={(e) => setFormData({...formData, image_url: e.target.value})}
              placeholder="https://..."
              className="mt-1"
            />
          </div>

          <div className="flex gap-3">
            <Button type="submit" className="flex-1 bg-orange-600 hover:bg-orange-700">
              {product ? 'Aggiorna' : 'Crea'} Prodotto
            </Button>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Annulla
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdminDashboard;
